let globalSheets: CSSStyleSheet[] | undefined;
const registeredSheets: CSSStyleSheet[] = [];

/**
 * Register a pre-built CSSStyleSheet so it is included in every shadow root.
 * Use this for cross-origin stylesheets whose cssRules are inaccessible.
 */
export const registerGlobalSheet = (sheet: CSSStyleSheet): void => {
  registeredSheets.push(sheet);
  globalSheets = undefined; // invalidate cache
};

/**
 * Clones all global <style> and <link> styles from the main document into constructable style sheets.
 * Caches the result after first call.
 */
export const getGlobalStyleSheets = (): readonly CSSStyleSheet[] => {
  if (globalSheets) return globalSheets;

  // Check if document.styleSheets exists and is iterable
  if (!document.styleSheets || typeof document.styleSheets.length !== "number") {
    globalSheets = [...registeredSheets];
    return globalSheets;
  }

  const cloned = [...document.styleSheets]
    .map((sheet) => {
      try {
        // Some styleSheets may not have cssRules (cross-origin without CORS, JSDOM, etc.)
        if (!sheet.cssRules) return null;
        const cssRules = [...sheet.cssRules].map((rule) => rule.cssText).join(" ");
        const newSheet = new CSSStyleSheet();
        newSheet.replaceSync(cssRules);
        return newSheet;
      } catch (error) {
        console.warn("[getGlobalStyleSheets] Failed to clone stylesheet:", sheet, error);
        return null;
      }
    })
    .filter((s): s is CSSStyleSheet => s !== null);

  globalSheets = [...registeredSheets, ...cloned];
  return globalSheets;
};

/**
 * Applies all global styles to the given ShadowRoot via adoptedStyleSheets.
 * Defensive: Only applies if adoptedStyleSheets is supported.
 */
export const addGlobalStylesToShadowRoot = (shadowRoot: ShadowRoot | null): void => {
  if (!shadowRoot) return;

  const sheets = getGlobalStyleSheets();
  // Defensive: Check if adoptedStyleSheets is supported
  if (Array.isArray(shadowRoot.adoptedStyleSheets) && sheets.length) {
    shadowRoot.adoptedStyleSheets.push(...sheets);
  }
};
